import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const AppointmentsContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";
const SLOT_MINUTES = 30;
const HIDDEN_STATUSES = new Set(["cancelled", "rescheduled"]);

const dayNames = ["Duminică", "Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă"];

const capitalize = (value = "") => value.charAt(0).toUpperCase() + value.slice(1);

const getDayName = (dateStr) => {
  const date = new Date(`${dateStr}T00:00:00`);
  const idx = date.getDay();
  const raw = dayNames[idx] || "";
  return raw ? capitalize(raw) : "";
};

const timeToHour = (time = "00:00") => {
  const [hh, mm] = time.split(":").map((v) => Number.parseInt(v, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh + mm / 60;
};

const hourToTime = (hour) => {
  const totalMinutes = Math.round((hour ?? 0) * 60);
  const hh = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const mm = String(totalMinutes % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

const expandAppointment = (appointment) => {
  if (!appointment) return [];

  const {
    id,
    date,
    startTime,
    durationMinutes = SLOT_MINUTES,
    hasRadiograph,
    category = "",
    recall = "none",
    notes = "",
    patientId,
    doctorEmail,
    createdByEmail,
    status,
    cancelNote,
    rescheduleNote,
    canceledAt,
    rescheduledAt,
    rescheduledTo,
    rescheduledFrom,
    patient = {},
  } = appointment;

  const slotsCount = Math.max(1, Math.round(durationMinutes / SLOT_MINUTES));
  const baseHour = timeToHour(startTime);
  const day = getDayName(date);

  return Array.from({ length: slotsCount }).map((_, index) => {
    const hour = baseHour + index * 0.5;
    const time = hourToTime(hour);
    const patientName = `${(patient.lastName || "").trim()} ${(patient.firstName || "").trim()}`.trim();

    return {
      id: index === 0 ? id : `${id}-p${index}`,
      mainAppointmentId: id,
      date,
      day,
      hour,
      time,
      duration: durationMinutes / 60,
      hasRadiograph,
      category,
      recall,
      notes,
      status,
      cancelNote,
      rescheduleNote,
      canceledAt,
      rescheduledAt,
      rescheduledTo,
      rescheduledFrom,
      doctorEmail,
      doctorName: appointment.doctorName,
      createdBy: createdByEmail,
      doctorDisplayName: appointment.doctorName || doctorEmail,
      patientId,
      patientCode: patient.code,
      lastName: patient.lastName,
      firstName: patient.firstName,
      phone: patient.phone,
      email: patient.email,
      cnp: patient.cnp,
      city: patient.city,
      isMinor: patient.isMinor,
      legalRepresentative: patient.legalRepresentative || {
        lastName: "",
        firstName: "",
        city: "",
        relation: "",
      },
      patientName,
    };
  });
};

const collapseSlots = (slots = []) => {
  const groups = new Map();
  slots.forEach((slot) => {
    const mainId = slot.mainAppointmentId || slot.id;
    if (!groups.has(mainId)) {
      groups.set(mainId, []);
    }
    groups.get(mainId).push(slot);
  });
  return groups;
};

export function AppointmentsProvider({ children }) {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [appointmentsRaw, setAppointmentsRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchAppointments = useCallback(async () => {
    if (!token) {
      setAppointments([]);
      setAppointmentsRaw([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/appointments`, {
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut încărca programările");
      }

      const list = data?.data?.appointments || [];
      setAppointmentsRaw(list);
      const slots = list
        .filter((app) => !HIDDEN_STATUSES.has(app.status))
        .flatMap(expandAppointment);
      setAppointments(slots);
    } catch (err) {
      console.error("Eroare fetch programări:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const upsertAppointmentGroup = useCallback(
    async ({
      existingAppointmentId = null,
      date,
      hour,
      duration = 0.5,
      patient,
      notes = "",
      recall = "none",
      hasRadiograph = false,
      category = "",
    }) => {
      if (!token) return null;
      if (!patient?.id || !date || typeof hour !== "number") {
        throw new Error("patientId, date and startTime are required");
      }

      const mainId = existingAppointmentId
        ? existingAppointmentId.includes("-p")
          ? existingAppointmentId.split("-p")[0]
          : existingAppointmentId
        : null;

      const payload = {
        patientId: patient?.id,
        date,
        startTime: hourToTime(hour),
        durationMinutes: Math.round(duration * 60),
        notes,
        recall,
        hasRadiograph,
        category,
      };

      const requestConfig = {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      };

      try {
        const res = await fetch(
          mainId
            ? `${API_URL}/api/appointments/${mainId}`
            : `${API_URL}/api/appointments`,
          {
            method: mainId ? "PUT" : "POST",
            ...requestConfig,
          }
        );
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut salva programarea");
        }

        const appointment = data?.data?.appointment;
        const slots = expandAppointment(appointment);

        setAppointmentsRaw((prev) => {
          const nextRaw = prev.filter((item) => item.id !== appointment?.id);
          nextRaw.push(appointment);
          return nextRaw;
        });
        setAppointments((prev) => {
          const filtered = prev.filter(
            (slot) => slot.mainAppointmentId !== appointment?.id
          );
          return [...filtered, ...slots];
        });

        return appointment?.id || null;
      } catch (err) {
        console.error("Eroare salvare programare:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const deleteAppointmentGroup = useCallback(
    async (anyAppointmentId) => {
      if (!anyAppointmentId || !token) return false;
      const mainId = anyAppointmentId.includes("-p")
        ? anyAppointmentId.split("-p")[0]
        : anyAppointmentId;

      try {
        const res = await fetch(`${API_URL}/api/appointments/${mainId}`, {
          method: "DELETE",
          headers: {
            ...authHeaders,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut șterge programarea");
        }
        setAppointments((prev) =>
          prev.filter((slot) => slot.mainAppointmentId !== mainId)
        );
        setAppointmentsRaw((prev) => prev.filter((item) => item.id !== mainId));
        return true;
      } catch (err) {
        console.error("Eroare ștergere programare:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const cancelAppointmentGroup = useCallback(
    async (anyAppointmentId, note = "") => {
      if (!anyAppointmentId || !token) return null;
      const mainId = anyAppointmentId.includes("-p")
        ? anyAppointmentId.split("-p")[0]
        : anyAppointmentId;

      const res = await fetch(`${API_URL}/api/appointments/${mainId}/cancel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify({ note }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut anula programarea");
      }
      const updated = data?.data?.appointment;
      if (updated?.id) {
        setAppointmentsRaw((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        setAppointments((prev) =>
          prev.filter((slot) => slot.mainAppointmentId !== updated.id)
        );
      }
      return updated || null;
    },
    [authHeaders, token]
  );

  const rescheduleAppointmentGroup = useCallback(
    async (anyAppointmentId, payload = {}) => {
      if (!anyAppointmentId || !token) return null;
      const mainId = anyAppointmentId.includes("-p")
        ? anyAppointmentId.split("-p")[0]
        : anyAppointmentId;

      const res = await fetch(`${API_URL}/api/appointments/${mainId}/reschedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-a putut reprograma programarea");
      }

      const newAppointment = data?.data?.appointment;
      const original = data?.data?.original;

      setAppointmentsRaw((prev) => {
        const filtered = prev.filter((item) => item.id !== original?.id);
        if (original) filtered.push(original);
        if (newAppointment) filtered.push(newAppointment);
        return filtered;
      });

      setAppointments((prev) => {
        const filtered = prev.filter((slot) => slot.mainAppointmentId !== mainId);
        const nextSlots = newAppointment ? expandAppointment(newAppointment) : [];
        return [...filtered, ...nextSlots];
      });

      return newAppointment?.id || null;
    },
    [authHeaders, token]
  );

  const moveAppointmentGroup = useCallback(
    async (mainAppointmentId, { day, date, hour }) => {
      if (!mainAppointmentId) return null;
      const groups = collapseSlots(appointments);
      const slots = groups.get(mainAppointmentId);
      if (!slots || slots.length === 0) return null;

      const primarySlot = slots.reduce((earliest, current) =>
        current.hour < earliest.hour ? current : earliest
      );

      const duration = slots.length * 0.5;

      return upsertAppointmentGroup({
        existingAppointmentId: primarySlot.id,
        day,
        date,
        hour,
        duration,
        patient: {
          id: primarySlot.patientId,
        },
        notes: primarySlot.notes,
        recall: primarySlot.recall,
        hasRadiograph: primarySlot.hasRadiograph,
        category: primarySlot.category || "",
      });
    },
    [appointments, upsertAppointmentGroup]
  );

  const resizeAppointmentGroup = useCallback(
    async (mainAppointmentId, newDuration) => {
      if (!mainAppointmentId || !newDuration || newDuration <= 0) return null;
      const groups = collapseSlots(appointments);
      const slots = groups.get(mainAppointmentId);
      if (!slots || slots.length === 0) return null;

      const primarySlot = slots.reduce((earliest, current) =>
        current.hour < earliest.hour ? current : earliest
      );

      return upsertAppointmentGroup({
        existingAppointmentId: primarySlot.id,
        day: primarySlot.day,
        date: primarySlot.date,
        hour: primarySlot.hour,
        duration: newDuration,
        patient: {
          id: primarySlot.patientId,
        },
        notes: primarySlot.notes,
        recall: primarySlot.recall,
        hasRadiograph: primarySlot.hasRadiograph,
        category: primarySlot.category || "",
      });
    },
    [appointments, upsertAppointmentGroup]
  );

  const selectAppointmentsByDate = useCallback(
    (dateStr) => appointments.filter((slot) => slot.date === dateStr),
    [appointments]
  );

  const selectAppointmentsByPatient = useCallback(
    (patientId) =>
      appointmentsRaw
        .filter((slot) => slot.patientId === patientId)
        .map((item) => ({
          ...item,
          time: item.startTime || item.time || "",
        })),
    [appointmentsRaw]
  );

  const selectAppointmentsForWeekDates = useCallback(
    (weekDates) => {
      const targetDates = new Set(weekDates.map((d) => d.toISOString().slice(0, 10)));
      return appointments.filter((slot) => targetDates.has(slot.date));
    },
    [appointments]
  );

  const syncPatientDetails = useCallback((patient) => {
    if (!patient?.id) return;
    setAppointments((prev) =>
      prev.map((slot) =>
        slot.patientId === patient.id
          ? {
              ...slot,
              patientCode: patient.code,
              lastName: patient.lastName,
              firstName: patient.firstName,
              phone: patient.phone,
              email: patient.email,
              cnp: patient.cnp,
              city: patient.city,
              isMinor: patient.isMinor,
              legalRepresentative: patient.legalRepresentative,
            }
          : slot
      )
    );
    setAppointmentsRaw((prev) =>
      prev.map((item) =>
        item.patientId === patient.id
          ? {
              ...item,
              patient: {
                ...(item.patient || {}),
                code: patient.code,
                lastName: patient.lastName,
                firstName: patient.firstName,
                phone: patient.phone,
                email: patient.email,
                cnp: patient.cnp,
                city: patient.city,
                isMinor: patient.isMinor,
                legalRepresentative: patient.legalRepresentative,
              },
            }
          : item
      )
    );
  }, []);

  const value = useMemo(
    () => ({
      appointments,
      appointmentsRaw,
      loading,
      error,
      refreshAppointments: fetchAppointments,
      upsertAppointmentGroup,
      deleteAppointmentGroup,
      cancelAppointmentGroup,
      rescheduleAppointmentGroup,
      moveAppointmentGroup,
      resizeAppointmentGroup,
      selectAppointmentsByDate,
      selectAppointmentsByPatient,
      selectAppointmentsForWeekDates,
      syncPatientDetails,
    }),
    [
      appointments,
      appointmentsRaw,
      loading,
      error,
      fetchAppointments,
      upsertAppointmentGroup,
      deleteAppointmentGroup,
      cancelAppointmentGroup,
      rescheduleAppointmentGroup,
      moveAppointmentGroup,
      resizeAppointmentGroup,
      selectAppointmentsByDate,
      selectAppointmentsByPatient,
      selectAppointmentsForWeekDates,
      syncPatientDetails,
    ]
  );

  return (
    <AppointmentsContext.Provider value={value}>
      {children}
    </AppointmentsContext.Provider>
  );
}

export const useAppointments = () => useContext(AppointmentsContext);
